import React from 'react';
import styled from 'styled-components';

import {
    FlexBox,
    FlexColumn
} from './StyledHeader.js';


/*
====================================
COLORES ANAMNESIS
====================================
*/

const colores = {

    teal: '#0E7A7E',

    naranja: '#E7973C',

    texto: '#2B2B2B',

    gris: '#646464',

    grisClaro: '#B5B5B0',

    linea: '#D9D9D9',

    fondo: '#FFFFFF'

};


/*
====================================
CAMPO ADAPTABLE
====================================

Los campos cuyo "name" contiene la palabra
"comentario" se renderizan automáticamente
como textarea.

Esto permite mantener intacto el JSX actual
sin tener que reemplazar decenas de campos
uno por uno.

Ejemplos:

partoComentario
enuresisComentario
relacionPadreComentario
duermeLuzComentario

Todos pasan automáticamente a textarea.

El resto continúa siendo input normal.
====================================
*/

const CampoAdaptable = React.forwardRef(
    (
        {
            name,
            type,
            ...props
        },
        ref
    ) => {

        const esComentario =
            typeof name === 'string' &&
            name
                .toLowerCase()
                .includes('comentario');


        if (esComentario) {

            return (
                <textarea
                    ref={ref}
                    name={name}
                    data-comentario="true"
                    {...props}
                />
            );

        }


        return (
            <input
                ref={ref}
                name={name}
                type={type}
                {...props}
            />
        );

    }
);


CampoAdaptable.displayName =
    'CampoAdaptable';


/*
====================================
CONTENEDOR GENERAL
====================================
*/

export const ContenidoAnamnesis = styled.div`

    width:
        100%;

    max-width:
        1180px;

    margin:
        0 auto;

    padding:
        8px
        42px
        0;

    box-sizing:
        border-box;

    font-family:
        'Nunito',
        Arial,
        sans-serif;


    @media (max-width: 768px) {

        padding:
            8px
            16px
            0;

    }

`;


/*
====================================
FORMULARIO
====================================
*/

export const FormularioAnamnesis = styled.form`

    width:
        100%;

    margin:
        0;

    padding:
        0;

`;


/*
====================================
BOTÓN DE SECCIÓN
====================================
*/

export const Boton = styled.button`

    position:
        relative;

    display:
        flex;

    align-items:
        center;

    width:
        100%;

    min-height:
        56px;

    margin:
        0;

    /*
    ====================================
    ESPACIO PARA EL CONTADOR
    ====================================

    Dejamos espacio a la derecha para
    mostrar 3/8, 10/12, etc., sin que
    se superpongan con el título.
    ====================================
    */

    padding:
        16px
        105px
        16px
        18px;

    border:
        none;

    background:
        transparent;

    color:
        ${colores.texto};

    font-family:
        'Nunito',
        Arial,
        sans-serif;

    font-size:
        13.5px;

    line-height:
        1.3;

    font-weight:
        800;

    letter-spacing:
        0.04em;

    text-align:
        left;

    text-transform:
        uppercase;

    cursor:
        pointer;

    /*
    Cuando usamos scrollIntoView(),
    dejamos aire respecto del header.
    */

    scroll-margin-top:
        90px;


    transition:
        color 0.15s ease,
        background 0.15s ease;


    &::before {

        content:
            '';

        width:
            8px;

        height:
            8px;

        margin-right:
            11px;

        flex:
            0 0
            auto;

        border-radius:
            50%;

        background:
            ${colores.linea};

    }


    &::after {

        /*
        Muestra el avance de la sección
        y la flecha para desplegarla.
        */

        content:
            attr(data-avance) '   ⌄';

        white-space:
            nowrap;

        position:
            absolute;

        top:
            50%;

        right:
            16px;

        color:
            ${colores.gris};

        font-size:
            18px;

        font-weight:
            700;

        transform:
            translateY(-54%);

        transition:
            transform 0.2s ease,
            color 0.2s ease;

    }


    &:hover {

        color:
            ${colores.teal};

        background:
            rgba(
                14,
                122,
                126,
                0.035
            );

    }


    &:focus {

        outline:
            none;

    }

`;


/*
====================================
SECCIÓN
====================================
*/

export const Fondo = styled.div`

    width:
        100%;

    margin:
        0;

    padding:
        0;

    box-sizing:
        border-box;

    background:
        ${colores.fondo};

    border-bottom:
        1px solid
        ${colores.linea};


    &:first-of-type {

        border-top:
            1.5px solid
            ${colores.texto};

    }


    /*
    Sección abierta.
    */

    &:has(
        > div:nth-child(2)
        > *
    ) ${Boton} {

        color:
            ${colores.teal};

    }


    &:has(
        > div:nth-child(2)
        > *
    ) ${Boton}::before {

        background:
            ${colores.teal};

    }


    /*
    ====================================
    CONTADOR DE SECCIÓN ABIERTA
    ====================================

    Cambiamos la flecha, pero no rotamos
    el elemento porque también contiene
    el número del avance.
    ====================================
    */

    &:has(
        > div:nth-child(2)
        > *
    ) ${Boton}::after {

        content:
            attr(data-avance) '   ⌃';

        color:
            ${colores.teal};

        transform:
            translateY(-54%);

    }

`;


/*
====================================
CUERPO DE LA SECCIÓN
====================================
*/

export const Margen = styled.div`

    width:
        100%;

    box-sizing:
        border-box;

    padding:
        12px
        22px
        30px
        38px;


    /*
    ====================================
    FLEXBOX
    ====================================

    Alineamos todo desde arriba.

    Esto es importante porque ahora los
    comentarios pueden ocupar varias líneas.
    */

    ${FlexBox} {

        width:
            100% !important;

        height:
            auto !important;

        min-height:
            42px !important;

        max-height:
            none !important;

        flex:
            0 0
            auto !important;

        margin:
            0
            0
            18px
            0 !important;

        padding:
            0 !important;

        display:
            flex !important;

        flex-direction:
            row !important;

        align-items:
            flex-start !important;

        justify-content:
            flex-start !important;

        gap:
            10px;

        box-sizing:
            border-box;

    }


    /*
    ====================================
    FLEXCOLUMN
    ====================================
    */

    ${FlexColumn} {

        width:
            100% !important;

        height:
            auto !important;

        min-height:
            0 !important;

        max-height:
            none !important;

        flex:
            0 0
            auto !important;

        margin:
            0
            0
            18px
            0 !important;

        padding:
            0 !important;

        display:
            flex !important;

        flex-direction:
            column !important;

        align-items:
            stretch !important;

        justify-content:
            flex-start !important;

        gap:
            0 !important;

        position:
            static !important;

        box-sizing:
            border-box;

    }


    ${FlexColumn} > * {

        flex:
            0
            0
            auto !important;

    }


    ${FlexColumn} ${FlexBox} {

        margin-bottom:
            18px !important;

    }


    ${FlexColumn} ${FlexBox}:last-child {

        margin-bottom:
            0 !important;

    }


    /*
    ====================================
    CONTENEDORES HISTÓRICOS
    ====================================
    */

    > div {

        width:
            100%;

        height:
            auto !important;

        min-height:
            0 !important;

        max-height:
            none !important;

        box-sizing:
            border-box;

    }


    > div > div {

        height:
            auto !important;

        min-height:
            0 !important;

        max-height:
            none !important;

        box-sizing:
            border-box;

    }


    /*
    ====================================
    TRATAMIENTOS PREVIOS
    ====================================

    Este selector corrige específicamente:

    Profesional 1
    [Psicopedagogía]
    Edad
    [4]

    Antes el select de especialidad quedaba
    prácticamente pegado al bloque de edad.

    Funciona tanto para los primeros dos
    profesionales que usan FlexColumn como
    para los demás que usan div.
    */

    select + ${FlexColumn},
    select + div {

        margin-top:
            16px !important;

    }


    /*
    Separación entre cada profesional.
    */

    > div > div > div {

        margin-bottom:
            24px;

    }


    > div > div > div:last-child {

        margin-bottom:
            0;

    }


    /*
    Labels como "Profesional 1".
    */

    > div > div > div > label {

        display:
            block;

        padding-top:
            0 !important;

        margin:
            0
            0
            8px;

        color:
            ${colores.gris};

        font-size:
            12.5px;

        font-weight:
            700;

    }


    /*
    ====================================
    ELIMINAR MÁRGENES VIEJOS
    ====================================
    */

    input,
    select,
    textarea {

        margin-top:
            0 !important;

        margin-bottom:
            0 !important;

        margin-left:
            0 !important;

        margin-right:
            0 !important;

    }


    /*
    ====================================
    INPUT NUMBER SIN FLECHAS
    ====================================
    */

    input[type='number'] {

        -moz-appearance:
            textfield;

        appearance:
            textfield;

    }


    input[type='number']::-webkit-inner-spin-button,
    input[type='number']::-webkit-outer-spin-button {

        -webkit-appearance:
            none;

        margin:
            0;

    }


    /*
    ====================================
    RESPONSIVE
    ====================================
    */

    @media (max-width: 768px) {

        padding:
            10px
            8px
            26px
            18px;


        ${FlexBox} {

            flex-wrap:
                wrap !important;

            gap:
                8px;

        }

    }

`;


/*
====================================
LABEL
====================================

Le damos padding superior para alinear
el texto con la primera línea del campo.

Cuando el comentario es grande no queda
centrado verticalmente en el textarea.
====================================
*/

export const Label = styled.label`

    display:
        block;

    flex:
        0
        0
        260px;

    min-width:
        0;

    margin:
        0
        14px
        0
        0;

    padding-top:
        11px;

    color:
        ${colores.gris};

    font-family:
        'Nunito',
        Arial,
        sans-serif;

    font-size:
        12.5px;

    line-height:
        1.4;

    font-weight:
        700;


    @media (max-width: 768px) {

        flex:
            1
            0
            100%;

        width:
            100%;

        margin:
            0
            0
            5px;

        padding-top:
            0;

    }

`;


/*
====================================
ESTILO BASE
====================================
*/

const estiloCampo = `

    box-sizing:
        border-box;

    height:
        42px;

    min-height:
        42px;

    padding:
        9px
        12px;

    border:
        1.5px solid
        ${colores.linea};

    border-radius:
        10px;

    outline:
        none;

    background:
        ${colores.fondo};

    color:
        ${colores.texto};

    font-family:
        'Nunito',
        Arial,
        sans-serif;

    font-size:
        13.5px;

    line-height:
        1.45;

    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;


    &:hover {

        border-color:
            ${colores.grisClaro};

    }


    &:focus {

        border-color:
            ${colores.teal};

        box-shadow:
            0 0 0 3px
            rgba(
                14,
                122,
                126,
                0.08
            );

    }


    &::placeholder {

        color:
            #A7A7A2;

    }


    &:disabled {

        background:
            #F5F5F3;

        color:
            ${colores.gris};

        cursor:
            not-allowed;

    }


    /*
    Los comentarios son textarea.

    Pueden mostrar varias líneas y además
    el profesional puede agrandarlos si
    necesita escribir información extensa.
    */

    &[data-comentario='true'] {

        height:
            72px;

        min-height:
            72px;

        max-height:
            220px;

        resize:
            vertical;

        overflow-y:
            auto;

        white-space:
            pre-wrap;

    }

`;


/*
====================================
INPUT
====================================
*/

export const Input = styled(CampoAdaptable)`

    ${estiloCampo}

    flex:
        1
        1
        0;

    width:
        100%;

    min-width:
        0;

`;


/*
====================================
COMENTARIO / INPUT PEQUEÑO
====================================

Cuando está junto a un select le damos
más espacio horizontal al comentario.

Ejemplo:

Enuresis
[ Sí ] [ comentario mucho más ancho ]
====================================
*/

export const InputSmall = styled(CampoAdaptable)`

    ${estiloCampo}

    flex:
        1.8
        1
        0;

    width:
        100%;

    min-width:
        220px;


    @media (max-width: 768px) {

        min-width:
            100%;

    }

`;


/*
====================================
SELECT
====================================
*/

export const Select = styled.select`

    ${estiloCampo}

    flex:
        0.9
        1
        0;

    width:
        100%;

    min-width:
        135px;

    cursor:
        pointer;


    /*
    Si el select está solo en la fila,
    igualmente ocupa todo el espacio
    disponible por flex-grow.
    */


    @media (max-width: 768px) {

        min-width:
            100%;

    }

`;


/*
====================================
SUBTÍTULO INTERNO
====================================
*/

export const SubtituloFormulario = styled.div`

    width:
        100%;

    height:
        auto !important;

    min-height:
        0 !important;

    margin:
        22px
        0
        16px !important;

    padding:
        0 !important;

    color:
        ${colores.gris};

    font-family:
        'Nunito',
        Arial,
        sans-serif;

    font-size:
        12.5px;

    line-height:
        1.4;

    font-weight:
        800;

    letter-spacing:
        0.025em;

    text-transform:
        uppercase;

`;


/*
====================================
BOTÓN GRABAR
====================================
*/

export const BotonSmall = styled.button`

    width:
        auto !important;

    min-width:
        140px;

    height:
        44px;

    min-height:
        44px;

    padding:
        10px
        28px;

    border:
        2px solid
        ${colores.naranja};

    border-radius:
        24px;

    background:
        ${colores.naranja};

    color:
        #FFFFFF;

    font-family:
        'Nunito',
        Arial,
        sans-serif;

    font-size:
        14px;

    line-height:
        1;

    font-weight:
        800;

    cursor:
        pointer;


    &:hover {

        background:
            #DD8930;

        border-color:
            #DD8930;

        box-shadow:
            0 5px 12px
            rgba(
                231,
                151,
                60,
                0.2
            );

    }


    &:focus {

        outline:
            none;

    }

`;


/*
====================================
BOTÓN GENERAR PDF
====================================

Botón secundario de Anamnesis.

Mantiene la estética del prototipo:
fondo blanco, borde teal y texto teal.
====================================
*/

export const BotonPdf = styled.button`

    display: inline-flex;

    align-items: center;

    justify-content: center;

    gap: 9px;

    width: auto;

    min-width: 160px;

    height: 44px;

    padding: 10px 24px;

    border: 2px solid ${colores.teal};

    border-radius: 24px;

    background: #FFFFFF;

    color: ${colores.teal};

    font-family: 'Nunito', Arial, sans-serif;

    font-size: 14px;

    font-weight: 800;

    cursor: pointer;

    transition:
        background 0.15s ease,
        color 0.15s ease;

    &:hover:not(:disabled) {
        background: #E9F5F4;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 3px solid rgba(14, 122, 126, 0.2);
        outline-offset: 2px;
    }

    @media (max-width: 480px) {
        min-width: 0;
        padding: 10px 16px;
        font-size: 12.5px;
    }

`;


/*
====================================
BARRA DE ACCIONES
====================================
*/

export const BarraAcciones = styled.div`

    position:
        sticky;

    bottom:
        0;

    z-index:
        20;

    display:
        flex;

    align-items:
        center;

    justify-content:
        flex-end;

    width:
        100%;

    margin:
        16px
        0
        0;

    padding:
        14px
        0;

    background:
        rgba(
            255,
            255,
            255,
            0.96
        );

    border-top:
        1px solid
        ${colores.linea};

    box-shadow:
        0 -7px 18px
        rgba(
            0,
            0,
            0,
            0.035
        );

    backdrop-filter:
        blur(5px);

`;